import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { announcementsApi, type AnnouncementFormData } from '../../services/announcementsApi'
import { controlCenterApi, type AccountListItem } from '../../services/controlCenterApi'
import { AnnouncementPreviewModal } from './AnnouncementPreviewModal'

const PRIORITY_OPTIONS = [
  { value: 'info' as const, label: 'Info', description: 'General information', color: 'border-blue-500 bg-blue-500/10 text-blue-400' },
  { value: 'warning' as const, label: 'Warning', description: 'Important notice', color: 'border-amber-500 bg-amber-500/10 text-amber-400' },
  { value: 'critical' as const, label: 'Critical', description: 'Urgent action needed', color: 'border-red-500 bg-red-500/10 text-red-400' },
]

function toLocalDatetime(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  const offset = d.getTimezoneOffset()
  const local = new Date(d.getTime() - offset * 60000)
  return local.toISOString().slice(0, 16)
}

export function AnnouncementForm(): React.ReactElement {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isEditing = Boolean(id)

  const [isLoading, setIsLoading] = useState(isEditing)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [showPreview, setShowPreview] = useState(false)
  const [showDealerPreview, setShowDealerPreview] = useState(false)

  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [startsAt, setStartsAt] = useState('')
  const [endsAt, setEndsAt] = useState('')
  const [priority, setPriority] = useState<'info' | 'warning' | 'critical'>('info')
  const [isSystemWide, setIsSystemWide] = useState(true)
  const [targetAccountIds, setTargetAccountIds] = useState<string[]>([])
  const [targetAccounts, setTargetAccounts] = useState<{ id: string; name: string }[]>([])
  const [accountSearch, setAccountSearch] = useState('')
  const [accountResults, setAccountResults] = useState<AccountListItem[]>([])
  const [searchingAccounts, setSearchingAccounts] = useState(false)

  const searchAccounts = useCallback(async (q: string) => {
    if (q.length < 2) {
      setAccountResults([])
      return
    }
    setSearchingAccounts(true)
    try {
      const data = await controlCenterApi.getAccounts({ q, status: 'active', per_page: 10 })
      setAccountResults(data.accounts.filter(a => !targetAccountIds.includes(a.id)))
    } catch {
      setAccountResults([])
    }
    setSearchingAccounts(false)
  }, [targetAccountIds])

  useEffect(() => {
    const timer = setTimeout(() => {
      if (accountSearch) searchAccounts(accountSearch)
      else setAccountResults([])
    }, 300)
    return () => clearTimeout(timer)
  }, [accountSearch, searchAccounts])

  const addTargetAccount = (account: { id: string; name: string }) => {
    setTargetAccountIds(prev => [...prev, account.id])
    setTargetAccounts(prev => [...prev, account])
    setAccountSearch('')
    setAccountResults([])
  }

  const removeTargetAccount = (accountId: string) => {
    setTargetAccountIds(prev => prev.filter(id => id !== accountId))
    setTargetAccounts(prev => prev.filter(a => a.id !== accountId))
  }

  useEffect(() => {
    if (!id) {
      // Default starts_at to now for new announcements
      setStartsAt(toLocalDatetime(new Date().toISOString()))
      return
    }

    const loadAnnouncement = async () => {
      setIsLoading(true)
      try {
        const ann = await announcementsApi.getAnnouncement(id)
        setTitle(ann.title)
        setBody(ann.body || '')
        setStartsAt(toLocalDatetime(ann.starts_at))
        setEndsAt(toLocalDatetime(ann.ends_at))
        setPriority(ann.priority)
        setIsSystemWide(ann.is_system_wide)
        setTargetAccountIds(ann.target_account_ids || [])
        setTargetAccounts(ann.target_accounts || [])
      } catch {
        setError('Failed to load announcement')
      }
      setIsLoading(false)
    }
    loadAnnouncement()
  }, [id])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim() || !startsAt) return

    setSubmitting(true)
    setError('')

    const data: AnnouncementFormData = {
      title: title.trim(),
      body,
      starts_at: new Date(startsAt).toISOString(),
      ends_at: endsAt ? new Date(endsAt).toISOString() : '',
      priority,
      is_system_wide: isSystemWide,
      target_account_ids: isSystemWide ? [] : targetAccountIds,
    }

    try {
      if (isEditing && id) {
        await announcementsApi.updateAnnouncement(id, data)
      } else {
        await announcementsApi.createAnnouncement(data)
      }
      navigate('/control_center/announcements')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save announcement')
    }
    setSubmitting(false)
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        <span className="text-slate-400 text-sm mt-3">Loading announcement...</span>
      </div>
    )
  }

  return (
    <div className="max-w-4xl">
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/control_center/announcements"
          className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-white transition-colors mb-3"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
          </svg>
          Back to Announcements
        </Link>
        <h1 className="text-2xl font-bold text-white">
          {isEditing ? 'Edit Announcement' : 'Create Announcement'}
        </h1>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main content - 2 columns */}
          <div className="lg:col-span-2 space-y-5">
            {/* Title */}
            <div className="bg-slate-800 rounded-xl border border-slate-700 p-5">
              <label htmlFor="ann-title" className="block text-sm font-medium text-slate-300 mb-1.5">
                Title <span className="text-red-400">*</span>
              </label>
              <input
                id="ann-title"
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="Announcement title..."
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                required
              />
            </div>

            {/* Body */}
            <div className="bg-slate-800 rounded-xl border border-slate-700 p-5">
              <div className="flex items-center justify-between mb-1.5">
                <label htmlFor="ann-body" className="block text-sm font-medium text-slate-300">
                  Body (HTML)
                </label>
                <button
                  type="button"
                  onClick={() => setShowPreview(!showPreview)}
                  className="text-xs text-primary hover:text-primary/80 transition-colors"
                >
                  {showPreview ? 'Edit' : 'Preview'}
                </button>
              </div>

              {showPreview ? (
                <div className="w-full min-h-[200px] bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-sm text-slate-200 prose prose-invert prose-sm max-w-none">
                  {body ? (
                    <div dangerouslySetInnerHTML={{ __html: body }} />
                  ) : (
                    <p className="text-slate-500 italic">No content to preview</p>
                  )}
                </div>
              ) : (
                <textarea
                  id="ann-body"
                  value={body}
                  onChange={e => setBody(e.target.value)}
                  placeholder="<p>Your announcement content here...</p>"
                  rows={8}
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary font-mono resize-y"
                />
              )}
              <p className="text-xs text-slate-500 mt-2">
                Allowed tags: p, br, strong, em, u, ol, ul, li, a, h1-h4, blockquote, code, pre
              </p>
            </div>
          </div>

          {/* Sidebar - 1 column */}
          <div className="space-y-5">
            {/* Priority */}
            <div className="bg-slate-800 rounded-xl border border-slate-700 p-5">
              <span className="block text-sm font-medium text-slate-300 mb-3">Priority</span>
              <div className="space-y-2">
                {PRIORITY_OPTIONS.map(opt => (
                  <label
                    key={opt.value}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                      priority === opt.value
                        ? opt.color + ' border-opacity-100'
                        : 'border-slate-600 hover:border-slate-500'
                    }`}
                  >
                    <input
                      type="radio"
                      name="priority"
                      value={opt.value}
                      checked={priority === opt.value}
                      onChange={() => setPriority(opt.value)}
                      className="sr-only"
                    />
                    <div>
                      <div className={`text-sm font-medium ${priority === opt.value ? '' : 'text-slate-300'}`}>
                        {opt.label}
                      </div>
                      <div className="text-xs text-slate-500">{opt.description}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Schedule */}
            <div className="bg-slate-800 rounded-xl border border-slate-700 p-5">
              <span className="block text-sm font-medium text-slate-300 mb-3">Schedule</span>
              <div className="space-y-3">
                <div>
                  <label htmlFor="ann-starts" className="block text-xs text-slate-500 mb-1">
                    Starts at <span className="text-red-400">*</span>
                  </label>
                  <input
                    id="ann-starts"
                    type="datetime-local"
                    value={startsAt}
                    onChange={e => setStartsAt(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary [color-scheme:dark]"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="ann-ends" className="block text-xs text-slate-500 mb-1">
                    Ends at <span className="text-slate-600">(optional)</span>
                  </label>
                  <input
                    id="ann-ends"
                    type="datetime-local"
                    value={endsAt}
                    onChange={e => setEndsAt(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary [color-scheme:dark]"
                  />
                  <p className="text-xs text-slate-500 mt-1">Leave empty for indefinite display</p>
                </div>
              </div>
            </div>

            {/* Targeting */}
            <div className="bg-slate-800 rounded-xl border border-slate-700 p-5">
              <span className="block text-sm font-medium text-slate-300 mb-3">Targeting</span>
              <label className="flex items-center justify-between cursor-pointer mb-4">
                <div>
                  <div className="text-sm text-slate-300">System-wide</div>
                  <div className="text-xs text-slate-500">Show to all accounts</div>
                </div>
                <div
                  className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer ${
                    isSystemWide ? 'bg-primary' : 'bg-slate-600'
                  }`}
                  onClick={() => setIsSystemWide(!isSystemWide)}
                  role="switch"
                  aria-checked={isSystemWide}
                  tabIndex={0}
                  onKeyDown={e => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); setIsSystemWide(!isSystemWide) } }}
                >
                  <div
                    className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                      isSystemWide ? 'translate-x-[22px]' : 'translate-x-0.5'
                    }`}
                  />
                </div>
              </label>

              {!isSystemWide && (
                <div>
                  <div className="border-t border-slate-700 pt-4">
                    <span className="block text-xs text-slate-500 mb-2">Target specific accounts</span>
                    <div className="relative">
                      <input
                        type="text"
                        value={accountSearch}
                        onChange={e => setAccountSearch(e.target.value)}
                        placeholder="Search accounts..."
                        className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                      />
                      {searchingAccounts && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                        </div>
                      )}

                      {/* Search results dropdown */}
                      {accountResults.length > 0 && (
                        <div className="absolute z-10 mt-1 w-full bg-slate-900 border border-slate-600 rounded-lg shadow-xl max-h-48 overflow-y-auto">
                          {accountResults.map(account => (
                            <button
                              key={account.id}
                              type="button"
                              onClick={() => addTargetAccount({ id: account.id, name: account.name })}
                              className="w-full text-left px-3 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-white transition-colors"
                            >
                              <div className="font-medium">{account.name}</div>
                              <div className="text-xs text-slate-500">{account.email}</div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Selected targets */}
                    {targetAccounts.length > 0 && (
                      <div className="mt-3 space-y-1.5">
                        {targetAccounts.map(account => (
                          <div
                            key={account.id}
                            className="flex items-center justify-between bg-slate-700/50 rounded-lg px-3 py-2"
                          >
                            <span className="text-sm text-slate-300 truncate">{account.name}</span>
                            <button
                              type="button"
                              onClick={() => removeTargetAccount(account.id)}
                              className="text-slate-500 hover:text-red-400 transition-colors shrink-0 ml-2"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {!isSystemWide && targetAccounts.length === 0 && (
                      <p className="text-xs text-amber-400 mt-2">No accounts selected. This announcement won&apos;t be visible to anyone.</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mt-5 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
            <span className="text-red-300 text-sm">{error}</span>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 mt-6 pt-5 border-t border-slate-700">
          <Link
            to="/control_center/announcements"
            className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
          >
            Cancel
          </Link>
          <button
            type="button"
            onClick={() => setShowDealerPreview(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-300 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Preview
          </button>
          <button
            type="submit"
            disabled={submitting || !title.trim() || !startsAt}
            className="px-5 py-2 text-sm font-semibold bg-primary hover:bg-primary/90 text-slate-900 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting
              ? (isEditing ? 'Saving...' : 'Creating...')
              : (isEditing ? 'Save Changes' : 'Create Announcement')
            }
          </button>
        </div>
      </form>

      {showDealerPreview && (
        <AnnouncementPreviewModal
          title={title}
          body={body || null}
          priority={priority}
          onClose={() => setShowDealerPreview(false)}
        />
      )}
    </div>
  )
}
